import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useState,
} from "react";

import { contractRowOfTheCustomer } from "shared/Funnel/contract-row-of-the-customer.helper";

import { useFunnelContextProviderOrderQuery } from "./funnel-context-provider-order.query.generated";

type FunnelContextValue = {
  isSwap: boolean;
  orderId?: number;
  parentOrderId?: number;
  loading: boolean;
  isContractSigned: boolean;
  signatureRequestInFlight: boolean;
  setIsContractSignedOnFrontend: Dispatch<SetStateAction<boolean>>;
  hasValidateKycInformation: boolean;
  setHasValidateKycInformation: Dispatch<SetStateAction<boolean>>;
};

const FunnelContext = createContext<FunnelContextValue>({
  isSwap: false,
  orderId: undefined,
  parentOrderId: undefined,
  loading: false,
  isContractSigned: false,
  signatureRequestInFlight: false,
  setIsContractSignedOnFrontend: () => {},
  setHasValidateKycInformation: () => {},
  hasValidateKycInformation: false,
});

type FunnelContextProviderProps = {
  isSwap?: boolean;
};

export const FunnelContextProvider = ({
  isSwap = false,
  children,
}: PropsWithChildren<FunnelContextProviderProps>) => {
  const { data: funnelOrderData, loading } = useFunnelContextProviderOrderQuery(
    {
      variables: {
        isNewOrder: !isSwap,
      },
      fetchPolicy: "network-only",
    },
  );
  const [isContractSignedOnFrontend, setIsContractSignedOnFrontend] =
    useState(false);
  const [hasValidateKycInformation, setHasValidateKycInformation] =
    useState(false);

  const contractRow = contractRowOfTheCustomer(
    funnelOrderData?.orders?.[0]?.contracts,
  );

  const isContractSigned =
    isContractSignedOnFrontend || !!contractRow?.signatureFinishedAt;

  const signatureRequestInFlight =
    !isContractSigned && !!contractRow?.signatureLink;

  const orderId = funnelOrderData?.orders?.[0]?.id;
  const parentOrderId =
    funnelOrderData?.orders?.[0]?.parentOrderId ?? undefined;

  return (
    <FunnelContext.Provider
      value={{
        isSwap,
        orderId,
        parentOrderId,
        loading,
        isContractSigned,
        signatureRequestInFlight,
        setIsContractSignedOnFrontend,
        hasValidateKycInformation,
        setHasValidateKycInformation,
      }}
    >
      {children}
    </FunnelContext.Provider>
  );
};

export const useFunnelContext = () => {
  const funnelContext = useContext(FunnelContext);

  if (funnelContext === undefined) {
    throw new Error("useContext must be used with a ContextProvider");
  }

  return funnelContext;
};

export const useUnsafeFunnelContext = () => {
  const { orderId, ...context } = useFunnelContext();

  if (!orderId) {
    throw new Error(
      `useUnsafeFunnelContext used with undefined orderId - context: ${JSON.stringify(
        context,
      )}`,
    );
  }

  return { orderId, ...context };
};
